import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  ShieldAlert, TrendingUp, AlertTriangle, Ban, UsersRound,
  School, Loader2, RefreshCw, Clock, FileText, UserX, Bell
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { apiService } from '@/services/api'  // ← Import direct du service API

// CORRECT localStorage keys for your app
const SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const YEAR_ID_KEY = 'lybibal-selected-year-id'
const YEAR_LABEL_KEY = 'lybibal-selected-year-label'

interface DisciplineStats {
  total_incidents: number
  total_sanctions: number
  total_convocations: number
  total_exclusions: number
  pending_incidents: number
  resolved_incidents: number
  incidents_by_month: Array<{ month: string; count: number }>
  top_students: Array<{ student_name: string; incident_count: number }>
  top_reasons: Array<{ reason: string; count: number }>
}

export default function DisciplineDashboardPage() {
  const { lang } = useLang()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<DisciplineStats | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  const selectedSchoolId = useMemo(() => {
    const stored = localStorage.getItem(SCHOOL_ID_KEY)
    if (stored) {
      const val = Number(stored)
      if (!isNaN(val) && val > 0) return val
    }
    const auth = localStorage.getItem('auth-storage')
    if (auth) {
      try {
        const parsed = JSON.parse(auth)
        const sid = parsed?.state?.user?.school_id || parsed?.state?.school_id
        if (sid) return Number(sid)
      } catch { /* ignore */ }
    }
    return null
  }, [])

  const selectedAcademicYearId = useMemo(() => {
    const stored = localStorage.getItem(YEAR_ID_KEY)
    if (stored) {
      const val = Number(stored)
      if (!isNaN(val) && val > 0) return val
    }
    const ay = localStorage.getItem('lybibal-academic-year')
    if (ay) {
      const val = Number(ay)
      if (!isNaN(val) && val > 0) return val
    }
    const auth = localStorage.getItem('auth-storage')
    if (auth) {
      try {
        const parsed = JSON.parse(auth)
        const yid = parsed?.state?.user?.academic_year_id || parsed?.state?.academic_year_id
        if (yid) return Number(yid)
      } catch { /* ignore */ }
    }
    return null
  }, [])

  const selectedYearLabel = useMemo(() => {
    return localStorage.getItem(YEAR_LABEL_KEY) || ''
  }, [])

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SCHOOL_ID_KEY || e.key === YEAR_ID_KEY || e.key === 'lybibal-academic-year') {
        window.location.reload()
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const loadStats = useCallback(async () => {
    if (!selectedSchoolId || !selectedAcademicYearId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      // CORRECTION : Appel direct via apiService au lieu de adminApi.getDisciplineStatistics
      const response = await apiService.get<any>('/discipline/statistics', {
        params: {
          school_id: selectedSchoolId,
          academic_year_id: selectedAcademicYearId
        }
      })
      setStats(response)
      setLastRefresh(new Date())
    } catch (err) {
      console.error('Error loading discipline stats:', err)
      toast.error(lang === 'fr' ? 'Erreur chargement statistiques' : 'Error loading statistics')
    } finally {
      setLoading(false)
    }
  }, [selectedSchoolId, selectedAcademicYearId, lang])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  useEffect(() => {
    const interval = setInterval(() => {
      loadStats()
    }, 60000)
    return () => clearInterval(interval)
  }, [loadStats])

  const $t = useMemo(() => ({
    fr: {
      title: 'Tableau de bord - Discipline',
      subtitle: 'Suivi des incidents',
      totalIncidents: 'Incidents',
      sanctions: 'Sanctions',
      convocations: 'Convocations',
      exclusions: 'Exclusions',
      pending: 'En attente',
      resolved: 'Résolus',
      lastRefresh: 'Dernière actualisation',
      refresh: 'Actualiser'
    },
    en: {
      title: 'Dashboard - Discipline',
      subtitle: 'Incident tracking',
      totalIncidents: 'Incidents',
      sanctions: 'Sanctions',
      convocations: 'Convocations',
      exclusions: 'Exclusions',
      pending: 'Pending',
      resolved: 'Resolved',
      lastRefresh: 'Last refresh',
      refresh: 'Refresh'
    }
  }), [])[lang]

  const displayStats = useMemo(() => {
    if (!stats) return null
    return {
      totalIncidents: stats.total_incidents?.toLocaleString() || '0',
      sanctions: stats.total_sanctions?.toLocaleString() || '0',
      convocations: stats.total_convocations?.toLocaleString() || '0',
      exclusions: stats.total_exclusions?.toLocaleString() || '0',
      pending: stats.pending_incidents?.toLocaleString() || '0',
      resolved: stats.resolved_incidents?.toLocaleString() || '0',
    }
  }, [stats])

  if (!selectedSchoolId || !selectedAcademicYearId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <School className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600">
            {lang === 'fr' ? 'Aucun établissement ou année scolaire sélectionné' : 'No school or academic year selected'}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {lang === 'fr' ? 'Veuillez sélectionner une année scolaire dans le header' : 'Please select an academic year in the header'}
          </p>
        </div>
      </div>
    )
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-12 h-12 text-amber-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/20 to-orange-50/20">
      <div className="page-header bg-white/80 backdrop-blur-md border-b border-amber-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl flex items-center justify-center shadow-lg shadow-amber-200">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-700 to-amber-900 bg-clip-text text-transparent">
                {$t.title}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {$t.subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {selectedYearLabel && (
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                <School className="w-3 h-3 mr-1.5" />
                {selectedYearLabel}
              </span>
            )}
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lastRefresh.toLocaleTimeString(lang === 'fr' ? 'fr-FR' : 'en-US')}
            </span>
            <button
              onClick={loadStats}
              className="flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors border border-amber-200"
            >
              <RefreshCw className="w-4 h-4" />
              {$t.refresh}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-amber-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{displayStats?.totalIncidents || '0'}</p>
                <p className="text-xs text-gray-500">{$t.totalIncidents}</p>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between">
              <span className="text-xs text-gray-400">{displayStats?.pending || '0'} {$t.pending.toLowerCase()}</span>
              <span className="text-xs text-emerald-600">{displayStats?.resolved || '0'} {$t.resolved.toLowerCase()}</span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-orange-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{displayStats?.sanctions || '0'}</p>
                <p className="text-xs text-gray-500">{$t.sanctions}</p>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                {lang === 'fr' ? 'Mesures disciplinaires' : 'Disciplinary measures'}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <UsersRound className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{displayStats?.convocations || '0'}</p>
                <p className="text-xs text-gray-500">{$t.convocations}</p>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                {lang === 'fr' ? 'Parents convoqués' : 'Parents summoned'}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-red-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{displayStats?.exclusions || '0'}</p>
                <p className="text-xs text-gray-500">{$t.exclusions}</p>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-xs text-red-600 font-medium">
                {lang === 'fr' ? 'Exclusions temporaires' : 'Temporary exclusions'}
              </p>
            </div>
          </div>
        </div>

        {stats && (
          <>
            {stats.incidents_by_month && stats.incidents_by_month.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-600" />
                  <h2 className="text-lg font-bold text-gray-800">
                    {lang === 'fr' ? 'Incidents par mois' : 'Incidents by month'}
                  </h2>
                </div>
                <div className="p-5">
                  <div className="space-y-3">
                    {stats.incidents_by_month.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="w-16 text-sm text-gray-600 font-medium">{item.month}</span>
                        <div className="flex-1">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-amber-500 transition-all"
                              style={{ 
                                width: `${Math.min(100, (item.count / (Math.max(...stats.incidents_by_month.map(i => i.count)) || 1)) * 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                        <span className="text-sm font-medium text-gray-700 w-8 text-right">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {stats.top_students && stats.top_students.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex items-center gap-2">
                  <UserX className="w-5 h-5 text-red-600" />
                  <h2 className="text-lg font-bold text-gray-800">
                    {lang === 'fr' ? 'Élèves avec le plus d\'incidents' : 'Students with most incidents'}
                  </h2>
                </div>
                <div className="p-5">
                  <div className="space-y-3">
                    {stats.top_students.slice(0, 5).map((student, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center text-xs font-bold text-red-700">
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700 font-medium">{student.student_name}</span>
                            <span className="font-medium">{student.incident_count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-red-500 transition-all"
                              style={{ 
                                width: `${Math.min(100, (student.incident_count / (stats.top_students[0]?.incident_count || 1)) * 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {stats.top_reasons && stats.top_reasons.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-orange-600" />
                  <h2 className="text-lg font-bold text-gray-800">
                    {lang === 'fr' ? 'Principaux motifs' : 'Top reasons'}
                  </h2>
                </div>
                <div className="p-5">
                  <div className="space-y-3">
                    {stats.top_reasons.slice(0, 5).map((reason, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-xs font-bold text-orange-700">
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700 font-medium">{reason.reason}</span>
                            <span className="font-medium">{reason.count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-orange-500 transition-all"
                              style={{ 
                                width: `${Math.min(100, (reason.count / (stats.top_reasons[0]?.count || 1)) * 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!stats && !loading && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {lang === 'fr' ? 'Aucune donnée disponible' : 'No data available'}
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {lang === 'fr'
                ? 'Les statistiques de discipline seront affichées ici une fois des incidents enregistrés.'
                : 'Discipline statistics will be displayed here once incidents are recorded.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}